import { useCallback, useEffect, useMemo } from 'react';
import { isEqual } from 'lodash-es';
import { useCallIntelLocations } from '../../../hooks/use-call-intel-locations';
import { useCallIntelFiltersContext } from '../../context/call-intel-filters.context';

export const useLocationFilterData = () => {
  const {
    allSelectedLocationIds,
    isLoading: isLoadingContext,
    updateFilterValues,
    updateFilterModified,
    updateErrors,
  } = useCallIntelFiltersContext();
  const { isLoading, isMultiLocation, locations, preselectedAccessibleLocations } = useCallIntelLocations();

  const defaultLocations = useMemo(
    () => Object.keys(preselectedAccessibleLocations || {}),
    [preselectedAccessibleLocations]
  );

  // `selectedLocations` uses `allSelectedLocationIds` (which is unfiltered) so that any selected/default
  // location IDs that are currently undergoing backfill are still visually shown as checked in the selector UI.
  const selectedLocations = useMemo(() => allSelectedLocationIds, [allSelectedLocationIds]);

  const onChange = useCallback(
    (locationIds: string[]) => {
      updateFilterValues({ locations: locationIds });
    },
    [updateFilterValues]
  );

  // Initialize filter selection with defaults if no locations are currently selected in the store
  useEffect(() => {
    if (!allSelectedLocationIds.length && defaultLocations.length) {
      updateFilterValues({ locations: defaultLocations });
    }
  }, [allSelectedLocationIds, defaultLocations, updateFilterValues]);

  useEffect(() => {
    updateFilterModified({
      locations: !isEqual(selectedLocations?.sort(), defaultLocations?.sort()),
    });
  }, [defaultLocations, selectedLocations, updateFilterModified]);

  return {
    defaultLocations,
    isLoading: isLoadingContext || isLoading,
    isMultiLocation,
    locations,
    selectedLocations,
    updateErrors,
    updateFilterModified,
    updateFilterValues,
    onChange,
  };
};
